import { observable, action, makeObservable } from 'mobx';
import { getNextInning } from 'services/helper-services/baseballLogicHelpers';
import { IInning } from '../../interfaces/tagging/IInning';
import { Play } from './Play';

export class InningManager {
  constructor() {
    makeObservable(this);
  }

  @observable inningsReversed: IInning[] = [];

  @observable playObjectIdThatTriggeredLastInningProgression = '';

  @action increaseHalfInning = (lastPlayObjectId = '') => {
    this.resetAnimationStatus();
    if (this.inningsReversed.length === 0) {
      this.inningsReversed.push({
        number: 1,
        isTop: true,
        playsReversed: [],
      });
    } else {
      const newInning = getNextInning(this.inningsReversed[0].number, this.inningsReversed[0].isTop, true);
      this.inningsReversed.unshift(newInning);
    }

    this.playObjectIdThatTriggeredLastInningProgression = lastPlayObjectId;
  };

  @action decreaseHalfInning = (): void => {
    this.resetAnimationStatus();
    if (this.getIsTopOfFirstInning()) {
    } else {
      const newInning = getNextInning(this.inningsReversed[0].number, this.inningsReversed[0].isTop, false);
      this.inningsReversed.unshift(newInning);
    }
  };

  @action removeLastHalfInning = () => {
    this.resetAnimationStatus();
    // if first inning or current inning has some live plays, don't allow unprogressing
    if (this.inningsReversed.length <= 1 || this.inningsReversed[0].playsReversed.some((p) => p.isLive)) {
      return;
    }
    const warmupAndDeletedPlaysToMove = [...this.inningsReversed[0].playsReversed];
    this.inningsReversed.splice(0, 1);
    const currentInning = this.inningsReversed[0];
    warmupAndDeletedPlaysToMove.forEach((p: Play) => {
      p.clearInningRelatedData();
      p.inningNumber = currentInning.number;
      p.isTopInning = currentInning.isTop;
      currentInning.playsReversed.push(p);
    });

    // start from 1 (meaning previous half-inning) and look for first play we can get
    this.playObjectIdThatTriggeredLastInningProgression = '';
    for (let i = 1; i < this.inningsReversed.length; i++) {
      for (let j = 0; j < this.inningsReversed[i].playsReversed.length; j++) {
        if (this.inningsReversed[i].playsReversed[j].isLive) {
          this.playObjectIdThatTriggeredLastInningProgression = this.inningsReversed[i].playsReversed[j].id;
          break;
        }
      }
    }
  };

  @action resetAnimationStatus = () => {
    this.inningsReversed.forEach((i) =>
      i.playsReversed.forEach((p) => {
        p.shouldAnimate = false;
      })
    );
  };

  getInning = (innningIndex: number) => this.inningsReversed[innningIndex];

  getPlay = (inningIndex: number, playIndex: number) => this.inningsReversed[inningIndex].playsReversed[playIndex];

  getLastPlayFromPreviousInning = (inningNumber: number, isTopInning: boolean) => {
    if (inningNumber === 1) {
      return undefined;
    }
    for (let i = 0; i < this.inningsReversed.length; i++) {
      if (
        this.inningsReversed[i].number < inningNumber &&
        this.inningsReversed[i].isTop === isTopInning &&
        this.inningsReversed[i].playsReversed.length > 0
      ) {
        return this.inningsReversed[i].playsReversed[0];
      }
    }
    return undefined;
  };

  getNextLivePlay = (play: Play) => {
    const { playIndex } = this.getPlayDataById(play.id);
    if (playIndex) {
      for (let i = 0; i < this.inningsReversed.length; i++) {
        for (let j = playIndex - 1; j >= 0; j--) {
          if (!this.inningsReversed[i].playsReversed[j].isWarmup) {
            return this.inningsReversed[i].playsReversed[j];
          }
        }
      }
      return undefined;
    }
  };

  getPreviousLivePlay = (play: Play) => {
    const { playIndex } = this.getPlayDataById(play.id);
    if (playIndex !== undefined) {
      for (let i = 0; i < this.inningsReversed.length; i++) {
        for (let j = playIndex + 1; j < this.inningsReversed[i].playsReversed.length; j++) {
          if (!this.inningsReversed[i].playsReversed[j].isWarmup) {
            return this.inningsReversed[i].playsReversed[j];
          }
        }
      }
      return undefined;
    }
  };

  getLastLivePlay = (onlyLivePlays: boolean) => {
    for (let i = 0; i < this.inningsReversed.length; i++) {
      for (let j = 0; j < this.inningsReversed[i].playsReversed.length; j++) {
        const play = this.inningsReversed[i].playsReversed[j];
        if (!onlyLivePlays && !play.isWarmup) {
          return play;
        }
      }
    }
    return undefined;
  };

  getLastPlay = (onlyLivePlays: boolean) => {
    for (let i = 0; i < this.inningsReversed.length; i++) {
      for (let j = 0; j < this.inningsReversed[i].playsReversed.length; j++) {
        const play = this.inningsReversed[i].playsReversed[j];
        if (!onlyLivePlays || !play.isWarmup) {
          return play;
        }
      }
    }
    return undefined;
  };

  getPlayDataById = (id: string) => {
    for (let i = 0; i < this.inningsReversed.length; i++) {
      for (let j = 0; j < this.inningsReversed[i].playsReversed.length; j++) {
        const play = this.inningsReversed[i].playsReversed[j];
        if (play.id === id) {
          return { play, inningIndex: i, playIndex: j };
        }
      }
    }
    return {};
  };

  getPlayByPlayId = (id: string) => {
    for (let i = 0; i < this.inningsReversed.length; i++) {
      for (let j = 0; j < this.inningsReversed[i].playsReversed.length; j++) {
        const play = this.inningsReversed[i].playsReversed[j];
        if (play.playId === id) {
          return play;
        }
      }
    }
    return null;
  };

  getPlayById = (id: string) => {
    return this.getPlayDataById(id).play;
  };

  getIsTopOfFirstInning = () => {
    if (this.inningsReversed.length <= 1 || (this.inningsReversed[0].isTop && this.inningsReversed[0].number <= 1)) {
      return true;
    }
    return false;
  };

  getLatestInningNumber = (): number => {
    return this.inningsReversed[0].number;
  };

  isFirstPlayInInning = (): boolean => {
    return this.inningsReversed[0].playsReversed.filter((play) => !play.isWarmup && !play.isDeleted).length === 0;
  };

  getFirstLivePlay = () => {
    return this.inningsReversed
      .at(-1)
      ?.playsReversed.filter((play) => !play.isWarmup && !play.isDeleted)
      .at(-1);
  };
}
